import { useSyncExternalStore } from 'react';
import { DomainError, toLocal, type BookingRecord, type Customer, type Phase } from '@nandanam/domain';
import { Engine, MemoryServer, allowedOffline, OneDriveRemote, RemoteError, SyncError, type FoundBusiness, type Graph, type Materialized, type Me, type Remote, type State } from '@nandanam/sync';
import { phase as phaseOf } from '@nandanam/domain';
import { IndexedDbStore, LocalStorageStore } from './local-stores';
import { parseHash, toHash, type Route, type RouteName } from './route';
import { demoState } from '../demo/seed';
import { BrowserFileStore, OneDriveFileStore, type FileStore } from './files';

export type Mode = 'demo' | 'onedrive';

export interface Overlay { name: string; params: Record<string, any> }
export interface ActResult { ok: boolean; verified?: boolean; error?: string; warnings?: string[]; opId?: string }

/**
 * Where a change is after the person pressed save. It is saved on this device at once; then
 *  - uploading: being sent to OneDrive and checked against other devices' changes
 *  - saved: in OneDrive and checked
 *  - device: kept on this device only (offline draft), uploads when back online
 *  - failed: not saved (the change has been taken back); `error` says why
 */
export interface SaveState { state: 'uploading' | 'saved' | 'device' | 'failed'; error?: string; at: number }

/** Persisted in-memory stand-in for OneDrive in demo mode, so the demo survives reloads like the prototype did. */
class DemoServer extends MemoryServer {
  static KEY = 'nf-demo-server-v1';
  static load(): DemoServer {
    const s = new DemoServer();
    try { const raw = localStorage.getItem(DemoServer.KEY); if (raw) Object.assign(s, JSON.parse(raw)); } catch { /* fresh */ }
    s.access = new Map([['demo', 'editor']]);
    return s;
  }
  persist() { try { localStorage.setItem(DemoServer.KEY, JSON.stringify({ files: this.files, clock: this.clock })); } catch { /* ignore */ } }
  override create(op: any) { this.clock = Math.max(this.clock, Date.now()); const m = super.create(op); this.persist(); return m; }
}

export class AppStore {
  mode: Mode;
  engine: Engine;
  route: Route = parseHash(location.hash);
  overlay: Overlay | null = null;
  overlaySeq = 0;
  toastMsg: string | null = null;
  savedFlash = false;
  now = Date.now();
  version = 0;
  account = { name: 'Owner', email: '' };
  private listeners = new Set<() => void>();
  private toastT: any;
  private savedT: any;
  server?: DemoServer;
  files: FileStore = new BrowserFileStore();
  remote: Remote;
  /** Real mode only. */
  graph?: Graph;
  me?: Me;
  biz?: FoundBusiness;
  /** Live status of each change saved in this session, by opId. */
  saves = new Map<string, SaveState>();
  /** Changes that turned out not to be saved, shown until dismissed. */
  problems: { opId: string; what: string; error: string }[] = [];
  /** A newer version of the app has been published; it loads as soon as nothing is in progress. */
  updateReady = false;
  /** Set when OneDrive asks for sign-in again; the sync bar then offers a Sign in button. */
  signInNeeded = false;

  constructor(mode: Mode, opts: { graph?: Graph; me?: Me; biz?: FoundBusiness; initial?: State } = {}) {
    this.mode = mode;
    if (mode === 'demo') {
      this.server = DemoServer.load();
      this.remote = this.server.client('demo', 'editor');
      this.account = { name: 'Owner (demo)', email: 'demo' };
      this.engine = new Engine({ remote: this.remote, local: new LocalStorageStore('nf-demo:'), accountId: 'demo', deviceId: 'demo-device', actor: 'Owner (demo)', numberStart: 1037, initial: demoState() });
    } else {
      const { graph, me, biz } = opts as Required<typeof opts>;
      this.graph = graph; this.me = me; this.biz = biz;
      const remote = new OneDriveRemote(graph, biz, { owner: biz.driveId.toLowerCase() === me.driveId.toLowerCase(), myUserId: me.id });
      this.remote = remote;
      this.files = new OneDriveFileStore(graph, biz, remote);
      this.account = { name: me.name, email: me.email };
      // One cache per Microsoft account and business folder: never mixed with another account or the demo.
      this.engine = new Engine({ remote, local: new IndexedDbStore(), accountId: `ms:${me.id}:${biz.folderId}`, deviceId: deviceId(), actor: me.name, initial: opts.initial });
    }
    this.engine.subscribe(() => this.bump());
    addEventListener('hashchange', () => { this.route = parseHash(location.hash); this.overlay = null; this.bump(); });
    setInterval(() => { this.now = Date.now(); this.bump(); }, 30_000);
    // Refresh on returning to the app.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refresh(); });
    addEventListener('online', () => this.refresh());
  }

  async start() {
    await this.engine.open();
    await this.refresh();
  }

  /** The OneDrive adapter, in real mode. */
  get onedrive(): OneDriveRemote | null { return this.remote instanceof OneDriveRemote ? this.remote : null; }
  get isOwner() { return this.onedrive?.isOwner ?? false; }

  private lastRefresh = 0;
  async refresh() {
    this.lastRefresh = Date.now();
    try { await this.engine.sync(); this.signInNeeded = false; } catch (e) {
      // Status already reflects the error.
      if (e instanceof RemoteError && e.kind === 'auth-expired') this.signInNeeded = true;
    }
    this.now = Date.now();
    this.bump();
  }

  subscribe = (f: () => void) => { this.listeners.add(f); return () => { this.listeners.delete(f); }; };
  notify() { this.bump(); }
  private bump() { this.version++; for (const f of this.listeners) f(); }

  get state(): State { return this.engine.state; }
  get view(): Materialized { return this.engine.view; }
  get canEdit() { return this.engine.access !== 'viewer' && this.engine.access !== 'none'; }

  /* lookups */
  rec = (id: string) => this.state.records.find(r => r.id === id);
  cust = (id: string): Customer => this.state.customers.find(c => c.id === id) ?? ({ id, name: 'Unknown', phone: '', whatsapp: '', alt: '', email: '', notes: '', since: '', docs: [] });
  pkg = (id: string) => this.state.settings.packages.find(p => p.id === id);
  phase = (r: BookingRecord): Phase => phaseOf(r, this.now);
  num = (r: BookingRecord) => r.number ?? (this.view.numbers.get(r.id)?.number || 'Draft');
  isProvisional = (r: BookingRecord) => this.view.numbers.get(r.id)?.provisional ?? false;
  isPendingRecord = (r: BookingRecord) => this.engine.pending.some(p => p.op.entity === `record:${r.id}`);
  nowLocal = () => toLocal(this.now);

  /* navigation */
  nav(name: RouteName, params: Record<string, string | number | undefined> = {}) {
    const clean: Record<string, string> = {};
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') clean[k] = String(v);
    location.hash = toHash({ name, params: clean });
  }
  open(name: string, params: Record<string, any> = {}) {
    this.overlay = { name, params }; this.overlaySeq++; this.bump();
    // Saves are checked against this device's copy first, so freshen it while the form is being filled in.
    if (Date.now() - this.lastRefresh > 20_000) void this.refresh();
  }
  close() { this.overlay = null; this.bump(); }
  toast(msg: string) {
    this.toastMsg = msg; this.bump();
    clearTimeout(this.toastT);
    this.toastT = setTimeout(() => { this.toastMsg = null; this.bump(); }, 3200);
  }

  get uploading() { let n = 0; for (const x of this.saves.values()) if (x.state === 'uploading') n++; return n; }

  /**
   * Saves a change. It is checked and saved on this device straight away, so the form can close at once,
   * and is uploaded to OneDrive in the background; `saves` and the save chip show how that goes. A change
   * that needs a connection and cannot be uploaded is taken back and listed in `problems`.
   */
  async act(cmd: string, entity: string, args: any, done?: string): Promise<ActResult> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false && !allowedOffline(cmd, args)) return { ok: false, error: 'No connection — not saved. This change needs a connection; try again when you are online.' };
    let q: Awaited<ReturnType<Engine['submit']>>;
    try {
      q = await this.engine.submit(cmd, entity, args);
    } catch (e) {
      if (e instanceof RemoteError && e.kind === 'auth-expired') this.signInNeeded = true;
      this.bump();
      return { ok: false, error: errorText(e) };
    }
    this.now = Date.now();
    this.track(q.opId, describe(cmd), q.done);
    if (done) this.toast(done);
    return { ok: true, verified: false, opId: q.opId };
  }

  /** Offline-capable change (new enquiry drafts, notes): the same as `act`; it stays on the device if offline. */
  draft(cmd: string, entity: string, args: any, done?: string): Promise<ActResult> { return this.act(cmd, entity, args, done); }

  private track(opId: string, what: string, done: Promise<{ verified: boolean; rejected: string | null }>) {
    const set = (x: Omit<SaveState, 'at'>) => { this.saves.set(opId, { ...x, at: Date.now() }); this.bump(); };
    const failed = (error: string) => { set({ state: 'failed', error }); this.problems = [...this.problems, { opId, what, error }]; this.toast(`${what} — not saved: ${error}`); };
    set({ state: 'uploading' });
    done.then(r => {
      if (r.rejected) failed(r.rejected);
      else if (r.verified) { set({ state: 'saved' }); this.flashSaved(); }
      else set({ state: 'device' });
    }, e => {
      if (e instanceof RemoteError && e.kind === 'auth-expired') this.signInNeeded = true;
      // A refusal may mean the sharing changed (for example to view-only): read it again.
      if (e instanceof RemoteError && e.kind === 'denied') void this.refresh();
      failed(errorText(e));
    });
  }

  dismissProblem(opId: string) { this.problems = this.problems.filter(p => p.opId !== opId); this.bump(); }

  private flashSaved() { this.savedFlash = true; clearTimeout(this.savedT); this.savedT = setTimeout(() => { this.savedFlash = false; this.bump(); }, 1500); }

  newId() { return this.engine.newEntityId(); }

  resetDemo() {
    if (this.mode !== 'demo') return;
    localStorage.removeItem(DemoServer.KEY);
    for (const k of Object.keys(localStorage)) if (k.startsWith('nf-demo:')) localStorage.removeItem(k);
    location.reload();
  }
}

const WHAT: Record<string, string> = {
  'customer.create': 'New customer', 'customer.update': 'Customer details', 'record.create': 'Enquiry', 'record.placeHold': 'Hold',
  'record.extendHold': 'Hold extension', 'record.releaseHold': 'Hold release', 'record.confirm': 'Booking confirmation',
  'record.adjustPricing': 'Pricing', 'record.payment': 'Payment', 'record.refund': 'Refund', 'record.voidPayment': 'Void payment',
  'record.notes': 'Notes', 'record.followUp': 'Follow-up date', 'record.guests': 'Guests', 'record.cancel': 'Cancellation',
  'record.markLost': 'Mark as lost', 'record.reopen': 'Reopen', 'record.collectDeposit': 'Deposit', 'record.settleDeposit': 'Deposit settlement',
  'record.checkout': 'Check-out', 'record.complete': 'Completion', 'record.addDoc': 'Document', 'record.logComm': 'WhatsApp log',
  'block.add': 'Date block', 'block.remove': 'Date block removal', 'expense.void': 'Void expense', 'investment.void': 'Void investment', 'settings.set': 'Settings', 'expense.add': 'Expense', 'investment.add': 'Investment',
};
const describe = (cmd: string) => WHAT[cmd] ?? 'Change';

export function errorText(e: unknown): string {
  if (e instanceof DomainError) return e.message;
  if (e instanceof SyncError) return e.message;
  if (e instanceof RemoteError) {
    if (e.kind === 'offline') return `No connection — not saved. Try again when you are online.${e.message.startsWith('No connection ') ? ` Details: ${e.message.slice(14)}` : ''}`;
    if (e.kind === 'quota') return 'OneDrive storage is full — not saved.';
    if (e.kind === 'denied') return `${e.message} — not saved.`;
    if (e.kind === 'auth-expired') return 'Sign-in expired — sign in again to save.';
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function deviceId(): string {
  try {
    let d = localStorage.getItem('nf-device-id');
    if (!d) { d = crypto.randomUUID(); localStorage.setItem('nf-device-id', d); }
    return d;
  } catch { return 'unknown-device'; }
}

let current: AppStore | null = null;
export function setStore(s: AppStore) { current = s; }
export function useStore(): AppStore {
  const s = current!;
  useSyncExternalStore(s.subscribe, () => s.version);
  return s;
}
